// src/widgets/TrafficChartWidget.jsx
// Fetch data via GET /dashboard/overview?widget=traffic_chart
//
// Kontrak backend (dashboard_api.rb, case 'traffic_chart'):
//   `present ch_query(sql), widget: widget` -> response BODY = ARRAY MENTAH, bukan { items: [...] }.
//
// Mode "total" (filter.group_by kosong):
//   [{ bucket, upload_bytes, download_bytes, total_bytes }, ...]
//
// Mode "grouped" (filter.group_by = username|nasipaddress|realm|server_name|groupname):
//   Nama kolom group SAMA PERSIS dengan filter.group_by, contoh untuk group_by=realm:
//   [{ bucket, realm, upload_bytes, download_bytes, total_bytes }, ...]
//   Backend hanya menerapkan top-N (filter.limit) saat group_by berisi TEPAT 1 kolom,
//   jadi widget ini hanya mengirim satu group_by sekaligus. Kita tetap slice top-N di
//   client sebagai pengaman tambahan (default limit 5).
import { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import Empty from './Empty';
import useWidgetData from '../hooks/useWidgetData';
import { fmtBucket } from '../utils/chartFormat';

const COLORS = ['var(--accent-blue)', 'var(--accent-cyan)', 'var(--accent-green)', 'var(--accent-orange)', 'var(--accent-purple, #a78bfa)'];

const GROUP_BY_LABELS = {
  username:     'Username',
  nasipaddress: 'NAS Device',
  realm:        'Realm',
  server_name:  'Server',
  groupname:    'Profile',
};

const fmtBytes = (b) => {
  if (!b || b === 0) return '0 B';
  const n = Number(b);
  if (n >= 1e9) return (n / 1e9).toFixed(1) + ' GB';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + ' MB';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + ' KB';
  return n + ' B';
};

export default function TrafficChartWidget({ filter = {} }) {
  const groupBy = filter.group_by || '';
  const limit   = filter.limit || 5;
  const { data, loading } = useWidgetData('traffic_chart', { ...filter, limit });

  // Backend mengembalikan array mentah, bukan { items: [...] }.
  const rawItems = Array.isArray(data) ? data : [];

  // ── Mode total: pakai langsung, cuma rename field ke upload/download ──
  // ── Mode grouped: pivot rows -> { bucket, [group]__up, [group]__down } + top-N group ──
  const { chartData, groups } = useMemo(() => {
    if (!groupBy) {
      const normalized = rawItems.map(row => ({
        bucket:   row.bucket,
        upload:   Number(row.upload_bytes ?? row.upload ?? 0),
        download: Number(row.download_bytes ?? row.download ?? 0),
      }));
      return { chartData: normalized, groups: [] };
    }

    const bucketMap = {};
    const totals = {};

    rawItems.forEach((row) => {
      const g    = row[groupBy] ?? row.group ?? row.label ?? 'lainnya';
      const up   = Number(row.upload_bytes   ?? row.upload   ?? 0);
      const down = Number(row.download_bytes ?? row.download ?? 0);

      if (!bucketMap[row.bucket]) bucketMap[row.bucket] = { bucket: row.bucket };
      bucketMap[row.bucket][`${g}__up`]   = up;
      bucketMap[row.bucket][`${g}__down`] = down;

      totals[g] = (totals[g] || 0) + up + down;
    });

    const topGroups = Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([g]) => g);

    const sorted = Object.values(bucketMap).sort((a, b) => new Date(a.bucket) - new Date(b.bucket));
    return { chartData: sorted, groups: topGroups };
  }, [rawItems, groupBy, limit]);

  const isEmpty = groupBy ? groups.length === 0 : chartData.length === 0;

  return (
    <div style={{
      padding: 16,
      height: '100%',
      boxSizing: 'border-box',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <TrendingUp size={13} color="var(--accent-blue)" />
        Traffic {groupBy ? `per ${GROUP_BY_LABELS[groupBy] || groupBy} (Top ${limit})` : ''}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        {loading
          ? <div className="skeleton" style={{ height: '100%' }} />
          : isEmpty
            ? <Empty />
            : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket}
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtBytes}
                    tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip
                    formatter={(v, n) => [fmtBytes(v), n]}
                    labelFormatter={fmtBucket}
                    contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11 }}
                  />
                  {groupBy && <Legend wrapperStyle={{ fontSize: 10 }} />}
                  {groupBy
                    ? groups.flatMap((g, i) => ([
                        <Line key={`${g}__up`}   type="monotone" dataKey={`${g}__up`}   name={`${g} ↑`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />,
                        <Line key={`${g}__down`} type="monotone" dataKey={`${g}__down`} name={`${g} ↓`} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} strokeDasharray="4 2" />,
                      ]))
                    : [
                        <Line key="upload"   type="monotone" dataKey="upload"   name="Upload"   stroke="var(--accent-blue)" strokeWidth={2} dot={false} />,
                        <Line key="download" type="monotone" dataKey="download" name="Download" stroke="var(--accent-cyan)" strokeWidth={2} dot={false} strokeDasharray="4 2" />,
                      ]
                  }
                </LineChart>
              </ResponsiveContainer>
            )
        }
      </div>
    </div>
  );
}
